import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency, CURRENCIES } from "@/context/CurrencyContext";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { StarRating } from "@/components/StarRating";

export default function ProfilePage() {
  const { user } = useAuth();
  const { currency, change, rate } = useCurrency();
  const [ratings, setRatings] = useState([]);
  const [refData, setRefData] = useState(null);
  useEffect(() => { if (user?.user_id) api.get(`/ratings/user/${user.user_id}`).then(r => setRatings(r.data.ratings)); }, [user]);
  useEffect(() => { api.get("/referrals/me").then(r => setRefData(r.data)).catch(() => {}); }, []);
  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="surface-card p-6">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-[color:var(--sage-subtle)] grid place-items-center text-2xl heading font-bold text-[color:var(--forest-deep)]">{(user.name || "?")[0]}</div>
          <div>
            <h1 className="heading text-2xl font-bold">{user.name}</h1>
            <div className="text-sm text-stone-500">{user.email} · <span className="uppercase mono text-xs">{user.role}</span></div>
            {user.organization && <div className="text-sm text-stone-600 mt-1">{user.organization}</div>}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 border-t pt-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Reputation</div>
            <div className="flex items-center gap-2"><StarRating value={user.rating_avg || 0} size="h-4 w-4" testId="profile-rating" /><span className="mono text-lg font-bold">{user.rating_avg?.toFixed(2) || "—"}</span></div>
            <div className="text-xs text-stone-500">{user.rating_count || 0} ratings</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Accreditation</div>
            <div className="mono text-sm uppercase">{user.accreditation_status}</div>
          </div>
        </div>
      </div>

      <div className="surface-card p-6 mt-6" data-testid="profile-currency-card">
        <h3 className="heading font-semibold mb-1">Display currency</h3>
        <p className="text-xs text-stone-500 mb-3">Prices &amp; fees are shown in your local currency via the inbuilt live converter (USD base). Sellers also set lot prices in this currency.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={currency} onValueChange={(c) => { change(c); toast.success(`Currency set to ${c}`); }}>
            <SelectTrigger data-testid="profile-currency-select" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="mono text-sm text-stone-500" data-testid="profile-rate">1 USD = {rate?.toFixed(4)} {currency}</span>
        </div>
      </div>

      <div className="surface-card p-6 mt-6" data-testid="profile-referral-card">
        <h3 className="heading font-semibold mb-1">Referral credits</h3>
        <p className="text-xs text-stone-500 mb-3">Earn <strong>3% of an invited business's first sale</strong> as account credit, then <strong>0.1% of every sale thereafter</strong>. Credits apply automatically at checkout and are <strong>not redeemable for cash</strong>.</p>
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Credit balance</div>
            <div className="mono text-xl font-bold text-emerald-700" data-testid="profile-credit-balance">${(refData?.credit_balance ?? 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Your referral code</div>
            <div className="mono text-lg font-bold" data-testid="profile-ref-code">{refData?.code ?? "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Businesses invited</div>
            <div className="mono text-lg font-bold">{refData?.referred?.length ?? 0}</div>
          </div>
        </div>
        {(refData?.referred?.length ?? 0) > 0 && (
          <ul className="mt-3 space-y-1 text-sm">
            {refData.referred.map((r, i) => (
              <li key={i} className="flex items-center justify-between border-b py-1.5">
                <span>{r.name} <span className="text-xs text-stone-400 mono">{r.role}</span></span>
                <span className={`text-xs mono uppercase px-2 py-0.5 rounded-full ${r.referral_credited ? "pill-recovering" : "pill-watch"}`}>{r.referral_credited ? "credited" : "pending first sale"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="surface-card p-6 mt-6">
        <h3 className="heading font-semibold mb-3">Ratings received</h3>
        {ratings.length === 0 ? <div className="text-stone-500 text-sm">No ratings yet.</div> :
          <ul className="space-y-3">
            {ratings.map(r => (
              <li key={r.id} className="border-b pb-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{r.from_user_name}</div>
                  <div className="mono text-sm">{"★".repeat(r.stars)}</div>
                </div>
                <div className="text-sm text-stone-600 mt-1">{r.comment}</div>
              </li>
            ))}
          </ul>}
      </div>
    </div>
  );
}
